/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, IButtonProps } from '@fluentui/react/lib/Button';
import { Icon } from '@fluentui/react/lib/Icon';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logIntentsFiltersPaneClosed, logIntentsFiltersPaneOpened } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { noop } from 'src/core/utils/base';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { PageSize } from 'src/luis/appShell/buildPane/intentPage/stores/IntentPageUIConfigStore';
import { FiltersSearch } from 'src/verticals/utterancesFilters/components/FiltersSearch';
import { MainFiltersBlade as MainFilterBlade } from 'src/verticals/utterancesFilters/components/MainFiltersBlade';
import { UtteranceFiltersStore } from 'src/verticals/utterancesFilters/stores/UtteranceFiltersStore';
import { FilterType, SelectionChange } from 'src/verticals/utterancesFilters/types/Filters';

export type DisplayedFilters = ('score' | 'entities' | 'intents' | 'nearestIntents' | 'difference' | 'pageCount')[];

export type FilterBladeProps = {
    /**
     * Optional header
     */
    headerElement?: JSX.Element;

    /**
     * Local store for the filter items states.
     */
    filtersStore: UtteranceFiltersStore;

    /**
     * The utterances count.
     */
    utterancesCount: number;

    /**
     * Handler function for items selection
     */
    onSelectionChange: (change: SelectionChange) => void;

    /**
     * Filter types/controls to display in the filters blade.
     */
    displayedFilters: DisplayedFilters;

    /**
     * The size of the utterances page.
     */
    pageSize?: PageSize;

    /**
     * Handler function for items selection
     */
    onChangePageSize?: (size: PageSize) => void;
};

const messages = defineMessages({
    entities: {
        id: 'utterancesFilters.filtersBlade.entities',
        defaultMessage: 'Entities'
    },
    intents: {
        id: 'utterancesFilters.filtersBlade.intents',
        defaultMessage: 'Intents'
    },
    allFilters: {
        id: 'utterancesFilters.filtersBlade.allFilters',
        defaultMessage: 'All filters'
    }
});

const scopedTheme = getScopedTheme('FiltersBlade');
const { styleComponent } = getStylistV2('FiltersBlade');

const Blade = styleComponent<IStackProps>(Stack)('Blade', { padding: 12, overflowY: 'auto' });

const BackButtonContainer = styleComponent<IButtonProps>(ActionButton)('BackButtonContainer', theme => ({
    color: theme.primaryColor,
    height: 16
}));

const BackButton = ({ onClick }: { onClick: () => void }) => {
    const { localize } = useLocalization();

    return (
        <BackButtonContainer onClick={onClick} primary>
            <Stack tokens={{ childrenGap: 4 }} verticalAlign="center" horizontal>
                <Icon iconName="Back" />
                <span>{localize(messages.allFilters)}</span>
            </Stack>
        </BackButtonContainer>
    );
};

/**
 * The filter blade parent component that render the filter blades
 */
export const FiltersBlade = observer((props: FilterBladeProps) => {
    const {
        filtersStore,
        utterancesCount,
        onSelectionChange,
        headerElement,
        displayedFilters,
        pageSize = 10,
        onChangePageSize = noop
    } = props;

    const filters = {
        score: filtersStore.scoreWindow,
        entities: filtersStore.entityFilterItems,
        nearestRivals: filtersStore.intentFilterItems,
        difference: filtersStore.differenceFilterItems
    };

    const { serviceName } = getPortalFromUrl();
    const { localize } = useLocalization();
    const [bladeId, setBladeId] = React.useState<FilterType>('general');

    React.useEffect(() => {
        logIntentsFiltersPaneOpened(serviceName);

        return () => logIntentsFiltersPaneClosed(serviceName);
    }, []);

    const handleSelectAll = (filterType: FilterType, isChecked: boolean) => filtersStore.selectAll(filterType, isChecked);

    let currentActiveBlade;

    switch (bladeId) {
        case 'entities':
            currentActiveBlade = (
                <Stack tokens={{ childrenGap: 16 }}>
                    <BackButton onClick={() => setBladeId('general')} />
                    <LuisText fontWeight={600} color={scopedTheme.titlesColor}>
                        {localize(messages.entities)}
                    </LuisText>
                    <FiltersSearch
                        showSelectAll
                        items={filters.entities}
                        onSelectionChange={onSelectionChange}
                        onToggleSelectAll={checked => handleSelectAll('entities', checked)}
                    />
                </Stack>
            );
            break;
        case 'intents':
            currentActiveBlade = (
                <Stack tokens={{ childrenGap: 16 }}>
                    <BackButton onClick={() => setBladeId('general')} />
                    <LuisText fontWeight={600} color={scopedTheme.titlesColor}>
                        {localize(messages.intents)}
                    </LuisText>
                    <FiltersSearch
                        showSelectAll
                        items={filters.nearestRivals}
                        onSelectionChange={onSelectionChange}
                        onToggleSelectAll={checked => handleSelectAll('intents', checked)}
                    />
                </Stack>
            );
            break;
        default:
            currentActiveBlade = (
                <MainFilterBlade
                    resultsCount={filtersStore.isFilterModeOn ? utterancesCount : 0}
                    filters={filters}
                    pageSize={pageSize}
                    onChangePageSize={onChangePageSize}
                    onOpenBladeClicked={setBladeId}
                    onSelectionChange={onSelectionChange}
                    displayedFilters={displayedFilters}
                />
            );
    }

    return (
        <Blade tokens={{ childrenGap: '12px' }}>
            {headerElement}

            {currentActiveBlade}
        </Blade>
    );
});
